import { OnboardingSlideProps, SLIDE_BACKGROUNDS } from "../onboarding.types";
import { PaginationDots } from "../PaginationDots";

/**
 * Commit Slide (Panel 1)
 *
 * First onboarding screen introducing the core concept:
 * "Bet on Yourself. Win Big."
 *
 * Design specs from panel.html:
 * - Font: Lexend (display font)
 * - Primary: uses app's primary color
 * - Background: #102210 (dark green)
 * - Title: 4rem, font-black, leading-[0.9], tracking-tighter, uppercase
 * - Subtitle: 2xl, font-bold, leading-tight, tracking-tight
 * - Description: base, font-normal, leading-relaxed, gray-300
 */

export function CommitSlide({ onNext, onSkip }: OnboardingSlideProps) {
  return (
    <div
      className="relative flex h-dvh w-full flex-col overflow-hidden bg-[#102210] text-white antialiased"
      style={{ fontFamily: '"Lexend", sans-serif' }}
    >
      {/* Background Layer - scale 1.1 for parallax effect */}
      <div className="absolute inset-0 z-0 origin-center scale-110">
        {/* Dark overlay for better text contrast */}
        <div className="absolute inset-0 bg-[#102210]/20 z-10 mix-blend-multiply" />

        {/* Background Image */}
        <img
          alt="Focused athlete sprinting on a dark track with dramatic lighting"
          className="h-full w-full object-cover object-center grayscale-[20%]"
          src={SLIDE_BACKGROUNDS.commit}
        />

        {/* Bottom gradient - strong fade to background */}
        <div className="absolute inset-0 z-20 bg-gradient-to-t from-[#102210] via-[#102210]/60 to-transparent" />

        {/* Top gradient - subtle darkening */}
        <div className="absolute inset-0 z-20 bg-gradient-to-b from-black/40 via-transparent to-transparent" />
      </div>

      {/* Skip Button - Top Right */}
      {/* Original: p-6 pt-12, adjusted for safe-area */}
      <div className="relative z-30 flex w-full items-center justify-end p-6 pt-[max(3rem,calc(env(safe-area-inset-top)+1rem))]">
        <button
          onClick={onSkip}
          className="flex items-center justify-center rounded-full bg-black/20 px-4 py-2 backdrop-blur-sm transition-colors hover:bg-white/10 active:scale-95"
        >
          <p className="text-sm font-bold tracking-wide uppercase text-primary/90">
            Skip
          </p>
        </button>
      </div>

      {/* Main Content - Bottom Aligned */}
      <div className="relative z-30 flex flex-1 flex-col justify-end p-6 pb-10">
        {/* Text Content */}
        <div className="mb-8 flex flex-col items-start gap-2">
          {/* Large Title - COMMIT */}
          <h1 className="tracking-tighter text-[4rem] font-black leading-[0.9] uppercase drop-shadow-lg text-primary">
            Commit
          </h1>

          {/* Subtitle */}
          <div className="flex flex-col gap-1">
            <p className="text-white text-2xl font-bold leading-tight tracking-tight">
              Bet on Yourself. <span className="text-primary">Win Big.</span>
            </p>
          </div>

          {/* Description */}
          <p className="mt-4 max-w-[90%] text-gray-300 text-base font-normal leading-relaxed">
            Join a run challenge.
          </p>
          <p className="max-w-[90%] text-gray-300 text-base font-normal leading-relaxed">
            Pledge your money. Hit your goals, you win a share of the pot or you
            lose.
          </p>
        </div>

        {/* Bottom Row - Pagination & Next Button */}
        <div className="flex w-full items-center justify-between pt-4">
          {/* Pagination Dots */}
          <PaginationDots total={3} activeIndex={0} />

          {/* Next Button */}
          <button
            onClick={onNext}
            className="group flex h-14 min-w-[120px] cursor-pointer items-center justify-center gap-3 overflow-hidden rounded-full px-6 transition-transform active:scale-95 bg-primary text-[#102210] shadow-lg shadow-primary/30"
          >
            <span className="text-base font-bold leading-normal tracking-wide">
              Next
            </span>
            <span
              className="material-symbols-outlined transition-transform group-hover:translate-x-1 w-6 h-6 flex items-center justify-center"
              style={{ fontSize: "24px" }}
            >
              arrow_forward
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
